package org.gymly.data

import org.gymly.model.AdminBookingItem
import org.gymly.model.AdminPortalActivation
import org.gymly.model.AdminBookingUpsert
import org.gymly.model.AdminBranch
import org.gymly.model.AdminTerminalReconcileAction
import org.gymly.model.AdminTerminalReconcileReport
import org.gymly.model.AdminTerminalReconcileResult
import org.gymly.model.AdminBranchUpsert
import org.gymly.model.AdminCatalogService
import org.gymly.model.AdminCatalogServiceUpsert
import org.gymly.model.AdminClient
import org.gymly.model.AdminClientTermsMonitoring
import org.gymly.model.AdminClientTermsRow
import org.gymly.model.AdminClientUpsert
import org.gymly.model.AdminTermsNoticeProgress
import org.gymly.model.AdminTermsNoticeResult
import org.gymly.model.AdminCompany
import org.gymly.model.AdminCompanyUpsert
import org.gymly.model.AdminDashboardSummary
import org.gymly.model.AdminEnrollmentReport
import org.gymly.model.AdminInventoryReport
import org.gymly.model.AdminKpi
import org.gymly.model.AdminListRow
import org.gymly.model.AdminMembershipAccessHistoryItem
import org.gymly.model.AdminMembershipItem
import org.gymly.model.AdminMembershipOrder
import org.gymly.model.AdminMembershipOrderStatus
import org.gymly.model.AdminMembershipOrderUpsert
import org.gymly.model.AdminPaymentMethod
import org.gymly.model.AdminMembershipPlanItem
import org.gymly.model.AdminMembershipPlanUpsert
import org.gymly.model.AdminNotificationEventCode
import org.gymly.model.AdminMembershipUpsert
import org.gymly.model.AdminMe
import org.gymly.model.AdminMeUpsert
import org.gymly.model.AdminMembershipReport
import org.gymly.model.AdminMembershipReportRow
import org.gymly.model.AdminCashReconciliationReport
import org.gymly.model.AdminNotificationFeed
import org.gymly.model.AdminNotificationItem
import org.gymly.model.AdminOrder
import org.gymly.model.AdminOrderUpsert
import org.gymly.model.AdminPayment
import org.gymly.model.AdminPaymentCollect
import org.gymly.model.AdminPaymentUpsert
import org.gymly.model.AdminPromotion
import org.gymly.model.AdminPromotionUpsert
import org.gymly.model.AdminReportExportFormat
import org.gymly.model.AdminReportFile
import org.gymly.model.AdminReportFilters
import org.gymly.model.AdminReportKind
import org.gymly.model.AdminRoute
import org.gymly.model.PickedImagePayload
import org.gymly.model.AdminSalesReport
import org.gymly.model.AdminScheduleSlot
import org.gymly.model.AdminScheduleUpsert
import org.gymly.model.AdminSearchHit
import org.gymly.model.AdminSessionReport
import org.gymly.model.AdminSessionReportRow
import org.gymly.model.AdminStaffAuditEvent
import org.gymly.model.AdminStaffAuditEventType
import org.gymly.model.AdminStaffAuditRecord
import org.gymly.model.AdminVisitItem
import org.gymly.model.AdminVisitUpsert
import org.gymly.model.AdminProduct
import org.gymly.model.AdminProductCategoryItem
import org.gymly.model.AdminProductUpsert
import org.gymly.model.AdminStoreSale
import org.gymly.model.AdminStoreSaleInvoiceFile
import org.gymly.model.AdminPaymentReceiptFile
import org.gymly.model.AdminStoreSaleStatus
import org.gymly.model.AdminStoreSaleUpsert
import org.gymly.model.AdminSettingsMail
import org.gymly.model.AdminSettingsMailUpsert
import org.gymly.model.AdminSettingsNotificationRule
import org.gymly.model.AdminSettingsNotificationRuleUpsert
import org.gymly.model.AdminSettingsNotificationTemplate
import org.gymly.model.AdminSettingsNotificationTemplateUpsert
import org.gymly.model.AdminSettingsPayment
import org.gymly.model.AdminSettingsPaymentUpsert
import org.gymly.model.AdminPaymentMethodType
import org.gymly.model.AdminPaymentMethodTypeCreate
import org.gymly.model.AdminPaymentMethodTypeUpsert
import org.gymly.model.AdminPaymentMethodsBundle
import org.gymly.model.AdminProductLicenseStatus
import org.gymly.model.AdminSettingsModules
import org.gymly.model.AdminSettingsModulesUpsert
import org.gymly.model.AdminAccessEvent
import org.gymly.model.AdminAccessSyncResult
import org.gymly.model.AdminAccessTerminal
import org.gymly.model.AdminAccessTerminalUpsert
import org.gymly.model.AdminAccessTestResult
import org.gymly.model.AdminApprovalRequest
import org.gymly.model.AdminSettingsRole
import org.gymly.model.AdminSettingsRoleUpsert
import org.gymly.model.AdminSettingsUser
import org.gymly.model.AdminSettingsUserType
import org.gymly.model.AdminSettingsUserUpsert
import org.gymly.model.AdminTrainer
import org.gymly.model.AdminTrainingAttendance
import org.gymly.model.AdminUser
import org.gymly.model.AdminWorker
import org.gymly.model.AdminWorkerUpsert
import org.gymly.model.PublicCompanyBranding
import org.gymly.model.PublicMarketingStats

interface AdminRepository {
    suspend fun login(email: String, password: String): AdminUser
    /** Monitoreo de términos de los socios (solo socios). Requiere Monitoreo de términos · Ver. */
    suspend fun fetchClientTermsMonitoring(): AdminClientTermsMonitoring
    /**
     * Reactivar, reenviar o suspender a un socio. Si quien lo pide no puede aprobar, lanza
     * AdminPendingApprovalException: la acción queda en Aprobaciones.
     */
    suspend fun applyClientTermsAction(clientId: String, op: String, motivo: String?): AdminClientTermsRow
    /**
     * Encola avisos de términos a los socios pendientes (scope PENDIENTES) o a la lista indicada (IDS), por los canales
     * dados. Si quien pide no puede aprobar, lanza AdminPendingApprovalException.
     */
    suspend fun sendClientTermsNotices(scope: String, clientIds: List<String>?, channels: List<String>): AdminTermsNoticeResult
    /** Avance de un lote de avisos en la cola. */
    suspend fun termsNoticeProgress(batchId: String): AdminTermsNoticeProgress
    /** Reanuda un canal de avisos pausado por fallos seguidos (EMAIL o WHATSAPP). Devuelve el monitoreo actualizado. */
    suspend fun resumeTermsNoticeChannel(channel: String): AdminClientTermsMonitoring
    /** Recuperación de contraseña: si el correo SMTP del gimnasio está configurado (público, sin sesión). */
    suspend fun passwordRecoveryAvailable(): Boolean
    suspend fun requestPasswordRecovery(scope: String, email: String)
    suspend fun confirmPasswordRecovery(scope: String, email: String, code: String, newPassword: String): String
    /** Stats del login (público, sin JWT). */
    suspend fun publicStats(): PublicMarketingStats
    /** Branding público del login (sin JWT). */
    suspend fun publicCompany(): PublicCompanyBranding
    suspend fun fetchMe(): AdminMe

    /** Renueva el token de personal; devuelve el nuevo token. Falla si la sesión venció. */
    suspend fun refreshSession(): String
    suspend fun updateMe(input: AdminMeUpsert): AdminMe
    /** Persiste solo el idioma, sin exigir el resto del perfil. */
    suspend fun updateMyLocale(preferredLocale: String): AdminMe
    suspend fun uploadMyPhoto(image: PickedImagePayload): AdminMe
    suspend fun fetchMyPhotoBytes(): ByteArray?
    /** Imagen guardada de un banner de marketing (la URL exige sesión). Null si no hay imagen. */
    suspend fun fetchPromotionImageBytes(fullUrl: String): ByteArray?
    suspend fun deleteMyPhoto()
    suspend fun changeMyPassword(currentPassword: String, newPassword: String)
    suspend fun dashboardSummary(): AdminDashboardSummary
    suspend fun dashboardKpis(): List<AdminKpi>
    suspend fun listTrainers(): List<AdminTrainer>
    suspend fun listSchedules(weekStart: String? = null): List<AdminScheduleSlot>
    suspend fun getSchedule(id: String): AdminScheduleSlot
    suspend fun createSchedule(input: AdminScheduleUpsert): AdminScheduleSlot
    suspend fun updateSchedule(id: String, input: AdminScheduleUpsert): AdminScheduleSlot
    suspend fun deleteSchedule(id: String)
    suspend fun setScheduleTrainerAbsent(id: String, absent: Boolean): AdminScheduleSlot
    suspend fun duplicateScheduleWeek(
        sourceWeekStart: String,
        targetWeekStart: String,
        replaceExisting: Boolean = false,
    ): Int
    suspend fun listBookings(from: String? = null, to: String? = null): List<AdminBookingItem>
    suspend fun getBooking(id: String): AdminBookingItem
    suspend fun createBooking(input: AdminBookingUpsert): AdminBookingItem
    suspend fun updateBooking(id: String, input: AdminBookingUpsert): AdminBookingItem
    suspend fun markBookingAttended(id: String): AdminBookingItem
    suspend fun markBookingNoShow(id: String): AdminBookingItem
    suspend fun deleteBooking(id: String)
    suspend fun listVisits(from: String? = null, to: String? = null): List<AdminVisitItem>
    suspend fun createVisit(input: AdminVisitUpsert): AdminVisitItem
    suspend fun deleteVisit(id: String)
    suspend fun search(
        query: String,
        trainerId: String? = null,
        planId: String? = null,
    ): List<AdminSearchHit>
    suspend fun notifications(): AdminNotificationFeed
    suspend fun markNotificationRead(id: String): AdminNotificationItem
    suspend fun markAllNotificationsRead(): AdminNotificationFeed
    suspend fun listClients(): List<AdminClient>
    suspend fun getClient(id: String): AdminClient
    suspend fun createClient(input: AdminClientUpsert): AdminClient
    suspend fun updateClient(id: String, input: AdminClientUpsert): AdminClient
    suspend fun deleteClient(id: String)
    suspend fun uploadClientPhoto(id: String, image: PickedImagePayload): AdminClient
    suspend fun fetchClientPhotoBytes(id: String): ByteArray?
    suspend fun deleteClientPhoto(id: String)

    /** Activa el acceso del socio al portal y a la app; devuelve la temporal solo si no se pudo enviar. */
    suspend fun activateClientPortal(clientId: String): AdminPortalActivation
    suspend fun listBranches(): List<AdminBranch>

    /** Compara los usuarios del terminal con Gymly y devuelve lo que sobra y lo que falta. */
    suspend fun reconcileTerminal(terminalId: String): AdminTerminalReconcileReport

    /** Aplica solo las acciones confirmadas por el administrador. */
    suspend fun applyTerminalReconcile(
        terminalId: String,
        actions: List<AdminTerminalReconcileAction>,
    ): AdminTerminalReconcileResult
    suspend fun getBranch(id: String): AdminBranch
    suspend fun createBranch(input: AdminBranchUpsert): AdminBranch
    suspend fun updateBranch(id: String, input: AdminBranchUpsert): AdminBranch
    suspend fun deleteBranch(id: String)
    suspend fun suggestPassword(): String
    suspend fun getCompany(): AdminCompany
    suspend fun updateCompany(input: AdminCompanyUpsert): AdminCompany
    suspend fun listSettingsUsers(): List<AdminSettingsUser>
    suspend fun getSettingsUser(type: AdminSettingsUserType, id: String): AdminSettingsUser
    suspend fun createSettingsUser(input: AdminSettingsUserUpsert): AdminSettingsUser
    suspend fun updateSettingsUser(
        type: AdminSettingsUserType,
        id: String,
        input: AdminSettingsUserUpsert,
    ): AdminSettingsUser
    suspend fun deleteSettingsUser(type: AdminSettingsUserType, id: String)
    suspend fun listSettingsRoles(): List<AdminSettingsRole>
    suspend fun getSettingsRole(id: String): AdminSettingsRole
    suspend fun createSettingsRole(input: AdminSettingsRoleUpsert): AdminSettingsRole
    suspend fun updateSettingsRole(id: String, input: AdminSettingsRoleUpsert): AdminSettingsRole
    suspend fun deleteSettingsRole(id: String)
    suspend fun listPendingApprovals(): List<AdminApprovalRequest>
    suspend fun listMyApprovals(): List<AdminApprovalRequest>
    suspend fun pendingApprovalsCount(): Long
    /** Asistente de IA: si está configurado en el servidor y si el usuario puede usarlo. */
    suspend fun estadoAsistente(): org.gymly.model.AsistenteEstado
    /** Asistente de IA: una pregunta con modelo. modo = "CONSULTA" o "PASOS". conversacionId null = conversación nueva. */
    suspend fun preguntarAsistente(
        pregunta: String,
        modo: String,
        conversacionId: String?,
    ): org.gymly.model.AsistenteRespuesta
    /**
     * Asistente de IA: modelos que el proveedor tiene disponibles. Con clave escrita usa esa;
     * sin clave escrita y con cuenta existente (proveedorId), usa la guardada.
     */
    suspend fun modelosDisponiblesAsistente(
        tipo: String,
        baseUrl: String?,
        clave: String?,
        proveedorId: String?,
    ): List<String>
    /** Asistente de IA: nombre, color y logo con los que se muestra. */
    suspend fun obtenerPersonalizacionAsistente(): org.gymly.model.AsistentePersonalizacion
    /** Asistente de IA: guarda nombre, color y logo. Requiere Configuración de seguridad. */
    suspend fun guardarPersonalizacionAsistente(datos: org.gymly.model.AsistentePersonalizacion): org.gymly.model.AsistentePersonalizacion
    /** Asistente de IA: conversaciones recientes de todos los usuarios, para auditoría. */
    suspend fun auditarConversacionesAsistente(): List<org.gymly.model.AsistenteConversacionAuditada>

    /** Asistente de IA: preguntas que no pudo responder con datos. Requiere Configuración de seguridad (ver). */
    suspend fun preguntasSinRespuestaAsistente(): List<org.gymly.model.AsistentePreguntaSinRespuesta>
    /** Asistente de IA: abre una conversación de otro usuario. Queda registrado en el servidor. */
    suspend fun abrirConversacionAuditadaAsistente(id: String): List<org.gymly.model.AsistenteMensaje>
    /** Asistente de IA: conversaciones del usuario actual, de la más reciente a la más antigua. */
    suspend fun listarConversacionesAsistente(): List<org.gymly.model.AsistenteConversacion>
    /** Asistente de IA: mensajes de una conversación propia. */
    suspend fun leerConversacionAsistente(id: String): List<org.gymly.model.AsistenteMensaje>
    /** Asistente de IA: enciende o apaga el asistente completo. Devuelve el valor guardado. */
    suspend fun cambiarEncendidoAsistente(habilitado: Boolean): Boolean
    /** Asistente de IA: configuración de los agentes (Configuración → Agentes). */
    suspend fun getConfiguracionAsistente(): org.gymly.model.AsistenteConfiguracion
    /** Asistente de IA: elimina una cuenta que no esté asignada a ningún agente. */
    suspend fun eliminarProveedorAsistente(id: String)
    /** Asistente de IA: crea un proveedor de modelo. */
    suspend fun crearProveedorAsistente(datos: org.gymly.model.AsistenteDatosProveedor): org.gymly.model.AsistenteProveedor
    /** Asistente de IA: edita un proveedor. datos.clave vacía conserva la clave guardada. */
    suspend fun actualizarProveedorAsistente(id: String, datos: org.gymly.model.AsistenteDatosProveedor): org.gymly.model.AsistenteProveedor
    /** Asistente de IA: edita la ficha de un agente (activo, pasos, herramientas y modelos). */
    suspend fun actualizarAgenteAsistente(id: String, datos: org.gymly.model.AsistenteDatosAgente): org.gymly.model.AsistenteAgente
    /** Asistente de IA: pregunta rápida (sin modelo). clave es una de las preguntas predefinidas del servidor. */
    suspend fun preguntarRapidaAsistente(clave: String, conversacionId: String?): org.gymly.model.AsistenteRespuesta
    suspend fun approveRequest(id: String, note: String? = null): AdminApprovalRequest
    suspend fun rejectRequest(id: String, note: String? = null): AdminApprovalRequest
    suspend fun getSettingsMail(): AdminSettingsMail
    suspend fun createSettingsMail(input: AdminSettingsMailUpsert): AdminSettingsMail
    suspend fun upsertSettingsMail(input: AdminSettingsMailUpsert): AdminSettingsMail
    suspend fun updateSettingsMail(id: String, input: AdminSettingsMailUpsert): AdminSettingsMail
    suspend fun deleteSettingsMail(id: String? = null)

    suspend fun getSettingsWhatsApp(): org.gymly.model.AdminSettingsWhatsApp
    suspend fun upsertSettingsWhatsApp(input: org.gymly.model.AdminSettingsWhatsAppUpsert): org.gymly.model.AdminSettingsWhatsApp
    suspend fun runWhatsAppWeeklyRemindersNow(): org.gymly.model.AdminWhatsAppRunNowResult
    suspend fun getSettingsPayments(): AdminSettingsPayment
    suspend fun upsertSettingsPayments(input: AdminSettingsPaymentUpsert): AdminSettingsPayment
    suspend fun deleteSettingsPayments(id: String? = null)
    suspend fun listPaymentMethodTypes(): AdminPaymentMethodsBundle
    suspend fun createPaymentMethodType(input: AdminPaymentMethodTypeCreate): AdminPaymentMethodType
    suspend fun updatePaymentMethodType(code: String, input: AdminPaymentMethodTypeUpsert): AdminPaymentMethodType
    suspend fun deletePaymentMethodType(code: String)
    suspend fun uploadPaymentMethodTypeImage(code: String, image: PickedImagePayload): AdminPaymentMethodType
    suspend fun upsertPaymentMethodGateways(input: AdminSettingsPaymentUpsert): AdminSettingsPayment
    suspend fun getSettingsModules(): AdminSettingsModules
    suspend fun updateSettingsModules(input: AdminSettingsModulesUpsert): AdminSettingsModules
    suspend fun getProductLicenseStatus(): AdminProductLicenseStatus
    suspend fun activateProductLicense(licenseCode: String): AdminProductLicenseStatus
    suspend fun listSettingsNotificationRules(): List<AdminSettingsNotificationRule>
    suspend fun replaceSettingsNotificationRules(
        rules: List<AdminSettingsNotificationRuleUpsert>,
    ): List<AdminSettingsNotificationRule>
    suspend fun listSettingsNotificationSchedules(): List<org.gymly.model.AdminSettingsNotificationSchedule>
    suspend fun replaceSettingsNotificationSchedules(
        schedules: List<org.gymly.model.AdminSettingsNotificationScheduleUpsert>,
    ): List<org.gymly.model.AdminSettingsNotificationSchedule>
    suspend fun listSettingsNotificationTemplates(): List<AdminSettingsNotificationTemplate>
    suspend fun getSettingsNotificationTemplate(eventCode: AdminNotificationEventCode): AdminSettingsNotificationTemplate
    suspend fun upsertSettingsNotificationTemplate(
        input: AdminSettingsNotificationTemplateUpsert,
    ): AdminSettingsNotificationTemplate
    suspend fun resetSettingsNotificationTemplate(eventCode: AdminNotificationEventCode): AdminSettingsNotificationTemplate
    suspend fun notificationTemplateVariablesHelp(): String
    suspend fun listAccessTerminals(): List<AdminAccessTerminal>
    suspend fun createAccessTerminal(input: AdminAccessTerminalUpsert): AdminAccessTerminal
    suspend fun updateAccessTerminal(id: String, input: AdminAccessTerminalUpsert): AdminAccessTerminal
    suspend fun deleteAccessTerminal(id: String)
    suspend fun testAccessTerminal(id: String): AdminAccessTestResult
    suspend fun configureAccessTerminalWebhook(id: String): AdminAccessTestResult
    suspend fun captureFaceFromTerminal(terminalId: String): PickedImagePayload
    suspend fun simulateAccessCheckIn(
        terminalId: String,
        clientId: String? = null,
        staffId: String? = null,
    ): AdminAccessEvent
    suspend fun closeAccessDoor(terminalId: String? = null): AdminAccessTestResult
    suspend fun listAccessEvents(limit: Int = 12): List<AdminAccessEvent>
    suspend fun syncClientAccess(clientId: String): AdminAccessSyncResult
    suspend fun syncStaffAccess(staffId: String): AdminAccessSyncResult
    suspend fun listProductCategories(): List<AdminProductCategoryItem>
    suspend fun createProductCategory(name: String): AdminProductCategoryItem
    suspend fun updateProductCategory(id: String, name: String): AdminProductCategoryItem
    suspend fun deleteProductCategory(id: String)
    suspend fun listProducts(): List<AdminProduct>
    suspend fun getProduct(id: String): AdminProduct
    suspend fun createProduct(input: AdminProductUpsert): AdminProduct
    suspend fun updateProduct(id: String, input: AdminProductUpsert): AdminProduct
    suspend fun deleteProduct(id: String)
    suspend fun listStoreSales(): List<AdminStoreSale>
    suspend fun getStoreSale(id: String): AdminStoreSale
    suspend fun createStoreSale(input: AdminStoreSaleUpsert): AdminStoreSale
    suspend fun updateStoreSale(id: String, input: AdminStoreSaleUpsert): AdminStoreSale
    /** Cobro de una venta pendiente; no exige permiso de Ventas, solo de Caja y cobros. */
    suspend fun recordStoreSalePayment(id: String, input: AdminStoreSaleUpsert): AdminStoreSale
    suspend fun approveStoreSale(id: String, status: AdminStoreSaleStatus): AdminStoreSale
    /** Validar comprobante de venta "Por validar": paid = Pagada; si no, Cancelada con motivo. */
    suspend fun validateStoreSaleProof(id: String, paid: Boolean, reason: String?)
    suspend fun uploadStoreSaleProof(id: String, image: PickedImagePayload): AdminStoreSale
    suspend fun deleteStoreSale(id: String)
    suspend fun downloadStoreSaleInvoice(id: String): AdminStoreSaleInvoiceFile
    suspend fun emailStoreSaleInvoice(id: String)
    suspend fun listMembershipOrders(): List<AdminMembershipOrder>
    suspend fun getMembershipOrder(id: String): AdminMembershipOrder
    suspend fun isMembershipPeriodCovered(
        clientId: String,
        planId: String,
        orderDate: String,
        excludeOrderId: String? = null,
    ): Boolean
    suspend fun createMembershipOrder(input: AdminMembershipOrderUpsert): AdminMembershipOrder

    /** Cobro de una orden pendiente; no exige permiso de Órdenes, solo de Caja y cobros. */
    suspend fun recordMembershipOrderPayment(
        id: String,
        status: AdminMembershipOrderStatus,
        method: AdminPaymentMethod?,
        pendingUsdCents: Long,
        pendingBsCents: Long,
    ): AdminMembershipOrder
    suspend fun updateMembershipOrder(id: String, input: AdminMembershipOrderUpsert): AdminMembershipOrder
    /** Corrige la fecha de un cobro de una orden pagada (ISO, p. ej. 2026-10-12T16:00:00Z). */
    suspend fun updateMembershipOrderPaymentDate(orderId: String, paymentId: String, paidAt: String): AdminMembershipOrder
    suspend fun markMembershipOrderPaid(id: String): AdminMembershipOrder
    suspend fun uploadMembershipOrderProof(id: String, image: PickedImagePayload): AdminMembershipOrder
    suspend fun deleteMembershipOrder(id: String)
    /** Quita del cuadre cobros de membresía cuya orden ya fue eliminada. */
    suspend fun cleanupOrphanMembershipPayments(): Int
    suspend fun downloadPaymentReceipt(id: String): AdminPaymentReceiptFile
    suspend fun emailPaymentReceipt(id: String)
    suspend fun listWorkers(): List<AdminWorker>
    suspend fun getWorker(id: String): AdminWorker
    suspend fun createWorker(input: AdminWorkerUpsert): AdminWorker
    suspend fun updateWorker(id: String, input: AdminWorkerUpsert): AdminWorker
    suspend fun deleteWorker(id: String)
    suspend fun uploadWorkerPhoto(id: String, image: PickedImagePayload): AdminWorker
    suspend fun fetchWorkerPhotoBytes(id: String): ByteArray?
    suspend fun deleteWorkerPhoto(id: String)
    suspend fun listServices(): List<AdminCatalogService>
    suspend fun getService(id: String): AdminCatalogService
    suspend fun createService(input: AdminCatalogServiceUpsert): AdminCatalogService
    suspend fun updateService(id: String, input: AdminCatalogServiceUpsert): AdminCatalogService
    suspend fun deleteService(id: String)
    suspend fun listMembershipPlans(): List<AdminMembershipPlanItem>
    suspend fun getMembershipPlan(id: String): AdminMembershipPlanItem
    suspend fun createMembershipPlan(input: AdminMembershipPlanUpsert): AdminMembershipPlanItem
    suspend fun updateMembershipPlan(id: String, input: AdminMembershipPlanUpsert): AdminMembershipPlanItem
    suspend fun deleteMembershipPlan(id: String)
    suspend fun listMemberships(): List<AdminMembershipItem>
    suspend fun getMembership(id: String): AdminMembershipItem
    suspend fun listMembershipAccessHistory(membershipId: String): List<AdminMembershipAccessHistoryItem>
    suspend fun createMembership(input: AdminMembershipUpsert): AdminMembershipItem
    suspend fun updateMembership(id: String, input: AdminMembershipUpsert): AdminMembershipItem
    suspend fun blockMembership(id: String, reason: String? = null): AdminMembershipItem
    suspend fun releaseMembershipAutomatic(id: String): AdminMembershipItem
    suspend fun deleteMembership(id: String)
    suspend fun registerTrainingSession(
        membershipId: String,
        sessionDate: String,
        attendance: AdminTrainingAttendance,
        serviceId: String? = null,
    )
    suspend fun listOrders(): List<AdminOrder>
    suspend fun getOrder(id: String): AdminOrder
    suspend fun createOrder(input: AdminOrderUpsert): AdminOrder
    suspend fun updateOrder(id: String, input: AdminOrderUpsert): AdminOrder
    suspend fun deleteOrder(id: String)
    suspend fun listPayments(): List<AdminPayment>
    suspend fun getPayment(id: String): AdminPayment
    /** Referencias de pago de ese día (yyyy-MM-dd) en pagos y ventas, con la orden o venta donde están. */
    suspend fun paymentReferencesOnDay(dateIso: String): List<org.gymly.api.dto.AdminPaymentReferenceDto>
    suspend fun createPayment(input: AdminPaymentUpsert): AdminPayment
    suspend fun collectPayments(input: AdminPaymentCollect): List<AdminPayment>
    suspend fun updatePayment(id: String, input: AdminPaymentUpsert): AdminPayment
    suspend fun uploadPaymentProof(id: String, image: PickedImagePayload): AdminPayment

    /** Guarda un comprobante de cobro que queda en aprobación y devuelve su ruta para la solicitud. */
    suspend fun stageMembershipOrderProof(image: PickedImagePayload): String
    suspend fun deletePayment(id: String)
    /** Validar comprobante de cobro "Por validar": paid = Pagado; si no, Rechazado con motivo. */
    suspend fun validatePaymentProof(id: String, paid: Boolean, reason: String?)
    suspend fun listPromotions(): List<AdminPromotion>
    suspend fun getPromotion(id: String): AdminPromotion
    suspend fun createPromotion(input: AdminPromotionUpsert): AdminPromotion
    suspend fun updatePromotion(id: String, input: AdminPromotionUpsert): AdminPromotion
    suspend fun deletePromotion(id: String)
    suspend fun replacePromotionBanner(id: String, image: PickedImagePayload): AdminPromotion
    suspend fun sessionReport(filters: AdminReportFilters): AdminSessionReport

    suspend fun listStaffAuditEvents(
        days: Int? = null,
        staffId: String? = null,
        moduleCode: String? = null,
        eventType: AdminStaffAuditEventType? = null,
    ): List<AdminStaffAuditEvent>

    suspend fun exportStaffAuditCsv(
        days: Int? = null,
        staffId: String? = null,
        moduleCode: String? = null,
        eventType: AdminStaffAuditEventType? = null,
    ): ByteArray

    suspend fun purgeStaffAudit(days: Int? = 90): Pair<Int, String>

    suspend fun recordStaffAuditEvent(input: AdminStaffAuditRecord)
    suspend fun membershipReport(filters: AdminReportFilters): AdminMembershipReport
    suspend fun enrollmentReport(filters: AdminReportFilters): AdminEnrollmentReport
    suspend fun cashReconciliationReport(filters: AdminReportFilters):AdminCashReconciliationReport
    suspend fun salesReport(filters: AdminReportFilters): AdminSalesReport
    suspend fun inventoryReport(filters: AdminReportFilters): AdminInventoryReport
    suspend fun exportReport(
        kind: AdminReportKind,
        format: AdminReportExportFormat,
        filters: AdminReportFilters,
    ): AdminReportFile
    suspend fun currentExchangeRate(): org.gymly.model.AdminExchangeRateQuote
    suspend fun exchangeRateSettings(): org.gymly.model.AdminExchangeRateSettings
    suspend fun updateExchangeRateSettings(input: org.gymly.model.AdminExchangeRateSettingsUpsert): org.gymly.model.AdminExchangeRateSettings
    /** Registra la tasa del día en el historial (base de la tasa manual). */
    suspend fun registerManualExchangeRate(effectiveDate: String, eurRate: Double, usdRate: Double): org.gymly.model.AdminExchangeRateSettings
    suspend fun refreshExchangeRates(): org.gymly.model.AdminExchangeRateQuote
    suspend fun convertDivisas(usdCents: Long): org.gymly.model.AdminDivisasConversion
    suspend fun listClientAccounts(): List<org.gymly.model.AdminClientAccountSummary>
    suspend fun clientAccountStatement(clientId: String): org.gymly.model.AdminClientAccountStatement
    suspend fun applyClientAccountCredit(
        clientId: String,
        input: org.gymly.model.AdminClientAccountApplyCredit,
    ): org.gymly.model.AdminClientAccountApplyCreditResult

    suspend fun depositToClientWallets(
        input: org.gymly.model.AdminClientWalletDepositBatch,
    ): org.gymly.model.AdminClientWalletDepositResult
    suspend fun listFor(route: AdminRoute): List<AdminListRow>
    fun subtitleFor(route: AdminRoute): String
    suspend fun setupAIProvider(input: org.gymly.api.dto.AdminAiProviderSetupRequestDto): String
    suspend fun listAIProviders(): List<org.gymly.ai.model.AIProvider>
    suspend fun listAIAgents(): List<org.gymly.ai.model.AIAgent>
    suspend fun createAIAgent(agent: org.gymly.ai.model.AIAgent): org.gymly.ai.model.AIAgent
    suspend fun updateAIProvider(id: Long, provider: org.gymly.ai.model.AIProvider): org.gymly.ai.model.AIProvider
    suspend fun deleteAIProvider(id: Long)
    suspend fun updateAIAgent(id: Long, agent: org.gymly.ai.model.AIAgent): org.gymly.ai.model.AIAgent
    suspend fun deleteAIAgent(id: Long)
    suspend fun listAgentFeatures(agentId: Long): List<org.gymly.ai.model.AIAgentFeature>
    suspend fun updateAgentFeature(
        agentId: Long,
        featureId: Long,
        update: org.gymly.ai.model.AIAgentFeatureUpdate
    ): org.gymly.ai.model.AIAgentFeature
    suspend fun listAIAuditLogs(): List<org.gymly.ai.model.AIAuditLog>
    suspend fun getExecutiveReport(): String
    suspend fun isAIEnabled(): Boolean
    suspend fun setAIEnabled(enabled: Boolean)
    fun logout()
}
